import { computeInventoryAppearanceHash, entityHeldObjectSpriteId } from "game-common/item/item"
import type { Entity } from "game-common/models"
import type { ClientRenderable } from "./entity_rendering_manager"
import { FireSprite } from "./sprites/fire_sprite"

type ArtworkState = Partial<Pick<Entity, "spriteId" | "expirationTs" | "quantity" | "landedArrow" | "landedArrowPitch">> &
    { landedArrowHeading?: number }

/** Visual changes shared by active entities and entities viewed on another floor. */
export const updateEntityArtwork = (
    sprite: ClientRenderable, entity: ArtworkState & Pick<Entity, "rainDamaged"> & Partial<Pick<Entity, "movement">>,
    previous: ArtworkState,
): ArtworkState => {
    if (previous.spriteId !== entity.spriteId) sprite.updateSpriteId?.(entity.spriteId)
    if (previous.expirationTs !== entity.expirationTs || previous.landedArrow !== entity.landedArrow)
        sprite.updateRotating?.(!entity.landedArrow && entity.expirationTs !== undefined)
    // A spent arrow is posed when its artwork is built; re-pose if a later message turns or tilts it.
    const heading = entity.landedArrow ? entity.movement?.angle ?? 0 : undefined
    if (entity.landedArrow && "landedArrowPitch" in previous &&
        (previous.landedArrowPitch !== entity.landedArrowPitch || previous.landedArrowHeading !== heading))
        sprite.poseLandedArrow?.(heading, entity.landedArrowPitch)
    if (previous.quantity !== entity.quantity) sprite.updateName?.(entity.quantity > 1 ? `${entity.quantity}` : "")
    if (sprite instanceof FireSprite) sprite.setSputtering(!!entity.rainDamaged)
    return { spriteId: entity.spriteId, expirationTs: entity.expirationTs, quantity: entity.quantity,
        landedArrow: entity.landedArrow, landedArrowPitch: entity.landedArrowPitch, landedArrowHeading: heading }
}

/** Both views must rebuild a body for the same equipment, held-object and water changes. */
export const entityAppearanceKey = (entity: Entity): string => (
    `${entity.submerged}-` +
    `${entity?.weapon?.weaponType}-` +
    `${entity?.appearance?.race}-` +
    `${entity?.appearance?.sex}-` +
    `${entity?.appearance?.hair}-` +
    `${entity?.appearance?.hairColor}-` +
    `${entity?.appearance?.facialHair}-` +
    `${entity?.appearance?.inventoryAppearanceHash}-` +
    `${entity?.appearance?.emberSprite}-` +
    // Equipment can arrive after appearance, without changing the server's appearance hash.
    `${computeInventoryAppearanceHash(entity.inventory)}-` +
    `${entityHeldObjectSpriteId(entity)}`
)
